// frontend/src/pages/SchoolProjects/TechnicianParts/IssueParts.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Chip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';

const steps = ['Select Technician & School', 'Add Parts', 'Review & Submit'];

const IssueParts = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [technicians, setTechnicians] = useState([]);
  const [parts, setParts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    technician_id: '',
    school_id: '',
    project_id: '',
    expected_return_date: '',
    issued_by: localStorage.getItem('erp_username') || 'Admin',
    notes: ''
  });
  const [selectedParts, setSelectedParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState({
    part_id: '',
    quantity: 1,
    notes: ''
  });
  const [availableStock, setAvailableStock] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.school_id) {
      const schoolProjects = projects.filter(p => p.school_id == formData.school_id);
      setFilteredProjects(schoolProjects);
      setFormData(prev => ({ ...prev, project_id: '' }));
    } else {
      setFilteredProjects([]);
    }
  }, [formData.school_id, projects]);

  useEffect(() => {
    // Update available stock whenever parts change
    const stock = {};
    parts.forEach(part => {
      stock[part.id] = part.quantity;
    });
    setAvailableStock(stock);
  }, [parts]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [techs, prts, schls, projs] = await Promise.all([
        api.getTechniciansList(),
        api.getComputerParts(),
        api.getSchools(),
        api.getSchoolProjects()
      ]);
      
      setTechnicians(techs.data || []);
      setParts(prts.data || []);
      setSchools(schls.data || []);
      setProjects(projs.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage({ type: 'error', text: 'Failed to load data. Please refresh.' });
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (e) => {
    setFormData(prev => ({
      ...prev,
      expected_return_date: e.target.value
    }));
  };

  const handlePartChange = (e) => {
    const { name, value } = e.target;
    setSelectedPart(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addPart = () => {
    if (!selectedPart.part_id) {
      setMessage({ type: 'error', text: 'Please select a part' });
      return;
    }

    const part = parts.find(p => p.id == selectedPart.part_id);
    if (!part) return;

    // Check if part already exists in selected parts
    const existingIndex = selectedParts.findIndex(p => p.part_id == selectedPart.part_id);
    
    if (existingIndex >= 0) {
      // Update existing part quantity
      const updatedParts = [...selectedParts];
      const newQuantity = updatedParts[existingIndex].quantity + selectedPart.quantity;
      
      // Check stock availability
      const available = availableStock[selectedPart.part_id] || 0;
      const totalUsed = selectedParts.reduce((sum, p) => 
        p.part_id == selectedPart.part_id ? sum + p.quantity : sum, 0
      ) + selectedPart.quantity;
      
      if (totalUsed > available) {
        setMessage({ 
          type: 'error', 
          text: `Cannot add ${selectedPart.quantity} more. Only ${available - (totalUsed - selectedPart.quantity)} available.` 
        });
        return;
      }
      
      updatedParts[existingIndex].quantity = newQuantity;
      updatedParts[existingIndex].notes = selectedPart.notes || updatedParts[existingIndex].notes;
      setSelectedParts(updatedParts);
    } else {
      // Check stock availability
      const available = availableStock[selectedPart.part_id] || 0;
      if (selectedPart.quantity > available) {
        setMessage({ 
          type: 'error', 
          text: `Insufficient stock. Available: ${available}, Requested: ${selectedPart.quantity}` 
        });
        return;
      }
      
      // Add new part
      setSelectedParts(prev => [...prev, {
        ...selectedPart,
        part_name: part.part_name,
        part_number: part.part_number,
        category: part.category
      }]);
    }

    // Reset form
    setSelectedPart({
      part_id: '',
      quantity: 1,
      notes: ''
    });
  };

  const removePart = (index) => {
    setSelectedParts(prev => prev.filter((_, i) => i !== index));
  };

  const updatePartQuantity = (index, delta) => {
    const updatedParts = [...selectedParts];
    const newQuantity = updatedParts[index].quantity + delta;
    
    if (newQuantity < 1) {
      removePart(index);
      return;
    }
    
    // Check stock availability
    const partId = updatedParts[index].part_id;
    const available = availableStock[partId] || 0;
    const totalUsed = selectedParts.reduce((sum, p, i) => 
      p.part_id == partId ? sum + (i === index ? newQuantity : p.quantity) : sum, 0
    );
    
    if (totalUsed > available) {
      setMessage({ 
        type: 'error', 
        text: `Cannot increase quantity. Only ${available} available total.` 
      });
      return;
    }
    
    updatedParts[index].quantity = newQuantity;
    setSelectedParts(updatedParts);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!formData.technician_id || !formData.school_id) {
        setMessage({ type: 'error', text: 'Please select technician and school' });
        return;
      }
    } else if (activeStep === 1) {
      if (selectedParts.length === 0) {
        setMessage({ type: 'error', text: 'Please add at least one part' });
        return;
      }
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (selectedParts.length === 0) {
      setMessage({ type: 'error', text: 'Please add parts to issue' });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        parts: selectedParts.map(p => ({
          part_id: p.part_id,
          quantity: p.quantity,
          notes: p.notes
        }))
      };

      const response = await api.bulkIssueParts(payload);
      
      setMessage({ 
        type: 'success', 
        text: `Successfully issued ${selectedParts.length} part(s) to technician` 
      });
      
      // Reset form
      setTimeout(() => {
        navigate('/school-projects/technician-parts/assignments');
      }, 2000);
      
    } catch (error) {
      console.error('Error issuing parts:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to issue parts. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getRemainingStock = (partId) => {
    const available = availableStock[partId] || 0;
    const used = selectedParts.reduce((sum, p) => 
      p.part_id == partId ? sum + p.quantity : sum, 0
    );
    return available - used;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/school-projects/technician-parts')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" gutterBottom>
            Issue Parts to Technician
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Assign computer parts to technicians for school projects
          </Typography>
        </Box>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 2 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        {activeStep === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Select Technician</InputLabel>
                <Select
                  name="technician_id"
                  value={formData.technician_id}
                  onChange={handleFormChange}
                  label="Select Technician"
                >
                  {technicians.map(tech => (
                    <MenuItem key={tech.id} value={tech.id}>
                      {tech.name} - {tech.specialization || 'General Technician'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Select School</InputLabel>
                <Select
                  name="school_id"
                  value={formData.school_id}
                  onChange={handleFormChange}
                  label="Select School"
                >
                  {schools.map(school => (
                    <MenuItem key={school.id} value={school.id}>
                      {school.school_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Project (Optional)</InputLabel>
                <Select
                  name="project_id"
                  value={formData.project_id}
                  onChange={handleFormChange}
                  label="Project (Optional)"
                  disabled={!formData.school_id}
                >
                  <MenuItem value="">None</MenuItem>
                  {filteredProjects.map(project => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.project_name} - {project.status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                name="expected_return_date"
                label="Expected Return Date"
                value={formData.expected_return_date}
                onChange={handleDateChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                name="notes"
                label="Assignment Notes"
                value={formData.notes}
                onChange={handleFormChange}
                placeholder="Enter any notes about this assignment..."
              />
            </Grid>
          </Grid>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Add Parts to Issue
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth>
                  <InputLabel>Select Part</InputLabel>
                  <Select
                    name="part_id"
                    value={selectedPart.part_id}
                    onChange={handlePartChange}
                    label="Select Part"
                  >
                    {parts
                      .filter(part => getRemainingStock(part.id) > 0)
                      .map(part => (
                        <MenuItem key={part.id} value={part.id}>
                          {part.part_name} ({part.part_number}) - 
                          Available: {getRemainingStock(part.id)} {getRemainingStock(part.id) <= 5 && '⚠️'}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  name="quantity"
                  label="Quantity"
                  value={selectedPart.quantity}
                  onChange={handlePartChange}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  name="notes"
                  label="Part Notes"
                  value={selectedPart.notes}
                  onChange={handlePartChange}
                  placeholder="Part-specific notes"
                />
              </Grid>
              
              <Grid item xs={12} md={1}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={addPart}
                  disabled={!selectedPart.part_id}
                  sx={{ height: '56px' }}
                >
                  <AddIcon />
                </Button>
              </Grid>
            </Grid>

            {selectedParts.length > 0 && (
              <Box mt={4}>
                <Typography variant="h6" gutterBottom>
                  Selected Parts ({selectedParts.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Part Name</TableCell>
                        <TableCell>Part Number</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedParts.map((part, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <InventoryIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                              {part.part_name}
                            </Box>
                          </TableCell>
                          <TableCell>{part.part_number}</TableCell>
                          <TableCell>
                            <Chip label={part.category} size="small" />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <IconButton 
                                size="small" 
                                onClick={() => updatePartQuantity(index, -1)}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Typography sx={{ mx: 1 }}>{part.quantity}</Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => updatePartQuantity(index, 1)}
                                disabled={getRemainingStock(part.part_id) <= 0}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {part.notes || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Remove">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => removePart(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Assignment
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Assignment Details
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>Technician:</strong> {technicians.find(t => t.id == formData.technician_id)?.name || 'N/A'}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>School:</strong> {schools.find(s => s.id == formData.school_id)?.school_name || 'N/A'}
                    </Typography>
                    {formData.project_id && (
                      <Typography variant="body1" gutterBottom>
                        <strong>Project:</strong> {projects.find(p => p.id == formData.project_id)?.project_name || 'N/A'}
                      </Typography>
                    )}
                    {formData.expected_return_date && (
                      <Typography variant="body1" gutterBottom>
                        <strong>Expected Return:</strong> {new Date(formData.expected_return_date).toLocaleDateString()}
                      </Typography>
                    )}
                    {formData.notes && (
                      <Typography variant="body1">
                        <strong>Notes:</strong> {formData.notes}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Parts Summary
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>Total Parts:</strong> {selectedParts.length}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Total Quantity:</strong> {selectedParts.reduce((sum, p) => sum + p.quantity, 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Parts List
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Part</TableCell>
                        <TableCell>Part Number</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedParts.map((part, index) => (
                        <TableRow key={index}>
                          <TableCell>{part.part_name}</TableCell>
                          <TableCell>{part.part_number}</TableCell>
                          <TableCell>
                            <Chip label={part.category} size="small" />
                          </TableCell>
                          <TableCell align="right">{part.quantity}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {part.notes || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" mt={4}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>
          
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || selectedParts.length === 0}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? 'Submitting...' : 'Issue Parts'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  (activeStep === 0 && (!formData.technician_id || !formData.school_id)) ||
                  (activeStep === 1 && selectedParts.length === 0)
                }
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={!!message.text}
        autoHideDuration={6000}
        onClose={() => setMessage({ type: '', text: '' })}
      >
        <Alert 
          severity={message.type}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IssueParts;